import React, { useState } from 'react';
import sizes from 'react-sizes';
import styled, { css } from 'styled-components';
import ReadMoreClientTestimonialsPopUp from 'components/Shared/ReadMoreClientTestimonialsPopUp';
import { MultiCarousel, SectionTitle } from 'components';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import QuotationMarksSVG from 'images/shared/misc/quotation_marks.svg';
import BlobPurpleYellow from 'images/home/client_testimonials/blob_purple_yellow.svg';
import LightPurpleBackground from 'images/shared/misc/light_purple_background.svg';

const ClientTestimonials = ({
  pathname,
  genericTestimonials,
  specificTestimonials,
  viewportWidth,
  paddingStyle,
  hideBackground,
  deeperMarginTop
}) => {
  const testimonials =
    specificTestimonials && specificTestimonials.length
      ? specificTestimonials
      : genericTestimonials;
  const [showFullQuote, setShowFullQuote] = useState(null);
  return (
    testimonials && (
      <Wrapper paddingStyle={paddingStyle} deeperMarginTop={deeperMarginTop}>
        {!hideBackground && <LightYellowBackground src={LightPurpleBackground} alt="light purple background" />}
        <SectionTitle title="Client Testimonials" paddingStyle="0 0 1.1em" />
        {pathname === '/' && viewportWidth > 550 && (
          <StyledBlobPurpleYellow src={BlobPurpleYellow} alt="purple and yellow blob" />
        )}
        {testimonials && (
          <MultiCarousel
            autoPlaySpeed={4000}
            leftArrowOffset={
              viewportWidth >= 1258
                ? '-1.1%'
                : viewportWidth >= 972
                ? '-6.75%'
                : viewportWidth >= 690
                ? '-10%'
                : 'calc(50% - 47.5px)'
            }
            rightArrowOffset={
              viewportWidth >= 1258
                ? 'calc(-1.1% + 6px)'
                : viewportWidth >= 972
                ? 'calc(-6.75% + 9px)'
                : viewportWidth >= 690
                ? 'calc(-10% + 8px)'
                : 'calc(50% - 47.5px)'
            }
            responsiveRules={{
              desktop: {
                breakpoint: { max: 10000, min: 1258 },
                items: 3
              },
              table: {
                breakpoint: { max: 1257, min: 972 },
                items: 2
              },
              mobile: {
                breakpoint: { max: 971, min: 0 },
                items: 1
              }
            }}>
            {testimonials.map((testimonial, i) => {
              const { personsName, title: companyName, companyLogo, quote } = testimonial;
              return (
                <TestimonialWrapper key={i}>
                  <TestimonialHeader>
                    <NameAndCompanyWrapper>
                      <Name>{personsName}</Name>
                      <Company>{companyName}</Company>
                    </NameAndCompanyWrapper>
                    {companyLogo && companyLogo.childImageSharp && (
                      <GatsbyImage image={getImage(companyLogo)} alt={`${companyName} logo`} />
                    )}
                  </TestimonialHeader>
                  <TestimonialBody>
                    <QuotationMarks src={QuotationMarksSVG} alt="quotation marks" />
                    <Quote>
                      {quote.length > 275 ? quote.slice(0, 275).trim() : quote}
                      {quote.length > 275 && (
                        <ReadMoreButton onClick={() => setShowFullQuote(quote)}>...</ReadMoreButton>
                      )}
                    </Quote>
                  </TestimonialBody>
                </TestimonialWrapper>
              );
            })}
          </MultiCarousel>
        )}
        <ReadMoreClientTestimonialsPopUp
          isOpen={showFullQuote}
          closeModal={() => setShowFullQuote(null)}
          viewportWidth={viewportWidth}
          quote={showFullQuote}
        />
      </Wrapper>
    )
  );
};

const Wrapper = styled.section`
  position: relative;
  ${({ paddingStyle }) => paddingStyle && `padding: ${paddingStyle}`};
  overflow: visible;
  padding: 3rem 1.25rem;
  margin-bottom: 3rem;
  margin-top: ${({ deeperMarginTop }) => (deeperMarginTop ? '3rem' : '0')};
  @media screen and (min-width: 768px) {
    margin-bottom: 6rem;
    margin-top: ${({ deeperMarginTop }) => (deeperMarginTop ? '6rem' : '0')};
  }
  @media screen and (min-width: 1600px) {
    margin-top: ${({ deeperMarginTop }) => (deeperMarginTop ? '4rem' : '0')};
  }

`;

const TestimonialWrapper = styled.div`
  box-shadow: 3px 3px 7px rgba(189, 189, 189, 0.4);
  display: flex;
  flex-direction: column;
  max-width: 545px;
  height: 100%;
  @media screen and (min-width: 972px) {
    max-width: 407.5px;
  }

  @media screen and (min-width: 1258px) {
    max-width: 360px;
  }

  p {
    font-size: 1rem;
  }
`;
const TestimonialHeader = styled.div`
  background-color: var(--dynamic-blue);
  color: white;
  padding: 0.75em 0.75em 0.75em 1.125em;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-radius: 10px 10px 0px 0px;
  min-height: 84px;
`;

const NameAndCompanyWrapper = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 258px;
`;

const Name = styled.p`
  color: #fff;
  font-weight: bold;
`;
const Company = styled.p`
  color: #fff;
  font-style: italic;
`;

const TestimonialBody = styled.div`
  background-color: white;
  padding: 1.75em 1.188em;
  height: 100%;
  font-style: italic;
  min-height: 243px;
  border-radius: 0px 0px 10px 10px;
`;

const QuotationMarks = styled.img`
  margin-bottom: 15px;
`;

const Quote = styled.p`
  color: var(--dynamic-dark-grey);
  margin-left: 1.36em;
  line-height: 1.25em;
  font-family: 'proxima-nova', sans-serif;
`;

const StyledBlobPurpleYellow = styled.img`
  bottom: -179px;
  position: absolute;
  right: 0;
`;

const LightYellowBackground = styled.img`
  position: absolute;
  left: 0;
  width: auto;
  height: 100%;
  @media screen and (min-width: 768px) {
    top: -40px;
    width: 100%;
    height: auto;
  }
  @media (min-width: 1600px) {
    top: -100px;
  }
`;

const ReadMoreButton = styled.button`
  background: none;
  border: none;
  color: var(--dynamic-blue);
  font-size: 1rem;
  font-style: italic;
  font-weight: 600;
  cursor: pointer;
  padding-left: 0.125rem;
`;

export default sizes(({ width }) => ({ viewportWidth: width && width }))(ClientTestimonials);
